import resourcesToBackend from 'i18next-resources-to-backend';

import {
  DEFAULT_LOCALE,
  I18N_NAMESPACE_KEYS,
  type I18nNamespace,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from '@repo/common/i18n';

/**
 * Every JSON under `packages/common/src/i18n/locales/<lng>/<ns>.json` becomes its own chunk.
 * Matches any future locale folder (de, fr, …) without editing this file.
 */
const localeNamespaceModules = import.meta.glob<{ default: Record<string, unknown> }>(
  '../../../common/src/i18n/locales/*/*.json',
);

function isSupportedLocale(language: string): language is SupportedLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(language);
}

/**
 * Map legacy i18next default namespace → our JSON files. Unknown namespaces reject so we
 * do not silently load `common` when a namespace key is wrong.
 */
function resolveLazyLoadNamespace(requestedNamespace: string): I18nNamespace | undefined {
  if (requestedNamespace === 'translation') {
    return 'common';
  }
  if ((I18N_NAMESPACE_KEYS as readonly string[]).includes(requestedNamespace)) {
    return requestedNamespace as I18nNamespace;
  }
  return undefined;
}

function findLocaleNamespaceModuleLoader(
  language: string,
  namespaceForFile: I18nNamespace,
): (() => Promise<{ default: Record<string, unknown> }>) | undefined {
  const relativePathSuffix = `/locales/${language}/${namespaceForFile}.json`;
  const entry = Object.entries(localeNamespaceModules).find(([path]) =>
    path.replace(/\\/g, '/').endsWith(relativePathSuffix),
  );
  return entry?.[1];
}

export const lazyLocaleBackend = resourcesToBackend((language: string, namespace: string) => {
  const languageBase = language.split('-')[0]?.toLowerCase() ?? language;
  if (languageBase === DEFAULT_LOCALE) {
    return Promise.reject(new Error(`Lazy loader called for bundled language "${language}" — should not happen`));
  }
  if (!isSupportedLocale(languageBase)) {
    return Promise.reject(new Error(`Unsupported locale: ${language} (normalized: ${languageBase})`));
  }
  const namespaceForFile = resolveLazyLoadNamespace(namespace);
  if (namespaceForFile === undefined) {
    return Promise.reject(
      new Error(
        `Unknown i18n namespace for lazy load: "${namespace}" (expected one of ${I18N_NAMESPACE_KEYS.join(', ')}, or legacy "translation")`,
      ),
    );
  }
  const loader = findLocaleNamespaceModuleLoader(languageBase, namespaceForFile);
  if (!loader) {
    return Promise.reject(
      new Error(
        `Missing locale bundle for locale="${language}" namespace="${namespace}" (file ns="${namespaceForFile}")`,
      ),
    );
  }
  return loader();
});
